import type {
  CustomRegionItem,
  ProductBbox,
  ProductListItem,
} from '@travel-planner/shared/schemas';
import {
  findRegionBoundary,
  toRegionOptions,
} from '@products/entities/custom-region/model/region-map-options';
import { ProductsMap } from '@products/widgets/products-map';
import { titleCaseRegionTitle } from '../model/region-layout';
import css from './advisor-planner-workspace.module.scss';

type AdvisorMapProps = {
  country: string;
  items: ProductListItem[];
  regionItems: CustomRegionItem[];
  selectedRegionIds: string[];
  focusedRegionId: string | null;
  moodRegionIds: string[];
  isLoading: boolean;
  isError: boolean;
  onSelectRegion: (regionId: string | null) => void;
  onProductClick: (productId: string) => void;
  /** User pans/zooms report the viewport; the map then stops auto-fitting. */
  onMoveEnd?: (bounds: ProductBbox) => void;
  /** The map's own region select; v3 picks regions with chips instead. */
  showRegionPicker?: boolean;
  /** Centre on the country by name (needs geocoding); else fit the results. */
  geocodeCountry?: boolean;
  /** `sync`: hovering a marker highlights its card instead of a tooltip. */
  markerHover?: 'tooltip' | 'sync';
  /** `soft`: grey region outlines instead of blue. */
  regionPalette?: 'blue' | 'soft';
};

type RegionTone = 'mood' | 'listed' | 'active';

export const AdvisorMap = ({
  country,
  items,
  regionItems,
  selectedRegionIds,
  focusedRegionId,
  moodRegionIds,
  isLoading,
  isError,
  onSelectRegion,
  onProductClick,
  onMoveEnd,
  showRegionPicker = true,
  geocodeCountry = true,
  markerHover = 'tooltip',
  regionPalette,
}: AdvisorMapProps) => {
  const listed = new Set(selectedRegionIds);
  const mooded = new Set(moodRegionIds);
  const layers = regionItems.flatMap((row) => {
    const ring = findRegionBoundary(regionItems, row.id);
    if (!ring || ring.length < 3) return [];
    const tone: RegionTone | null =
      row.id === focusedRegionId
        ? 'active'
        : listed.has(row.id)
          ? 'listed'
          : mooded.has(row.id)
            ? 'mood'
            : null;
    return tone ? [{ points: ring, tone }] : [];
  });
  const hasActiveRing = layers.some((layer) => layer.tone === 'active');
  return (
    <section aria-label="Products map" className={css.mapPane}>
      <ProductsMap
        focusCountry={hasActiveRing || !geocodeCountry ? null : country}
        isError={isError}
        isLoading={isLoading}
        onMoveEnd={onMoveEnd}
        markerHover={markerHover}
        onProductClick={onProductClick}
        onRegionChange={onSelectRegion}
        products={items}
        regionLayers={layers}
        regionPalette={regionPalette}
        regionOptions={
          showRegionPicker
            ? toRegionOptions(regionItems).map((option) => ({
                ...option,
                label: titleCaseRegionTitle(option.label),
              }))
            : undefined
        }
        selectedRegionId={focusedRegionId}
      />
    </section>
  );
};
